/** Platform specific implementations. */
import * as Api from 'mytours-read-api-typescript'
import { Middleware, ReducersMapObject } from 'redux'
import { connectRouter, RouterState } from 'connected-react-router'
import {
	AppMetaData,
	getCurrentLanguageCode,
	getStore,
	FileType,
	StoreState,
	DevicePlatform,
	DEVICE_PLATFORM_KEY_MOBILE_WEB,
} from 'mytours-read-common'
import { getUAParser } from '../../services/uaParser'

import { colorMix, darkenOrLighten, fade, isDark } from 'common_lib/services/color'
import { loggerMiddleware } from '../logging/index'
import { middleware as routerMiddleware } from '../navigation/index'
import settings from '../settings/functions'
import strings from '../../strings'
import { FileRec, GetAppShowResponse } from 'mytours-read-api-typescript'
import { ImageSizePreset } from 'common_lib/types'
import { generateImageUrl } from 'common_lib/utils'
import { getDemoModeState } from '../common/functions'
import { history } from '../navigation/history'

// Received from backend, declared in views/layouts/mobile_web.html.erb
declare const projectId: string
declare const token: string
declare const baseServer

interface FormattedDurationObject {
	days?: number
	hours?: number
	minutes?: number
}

export interface PlatformStoreState extends StoreState {
	router: RouterState // added by connectRouter(history)
}

/** Customise the Redux middleware for this platform */
export function customiseReduxMiddleware(middlewares: Middleware[]): Middleware[] {
	// navigation
	middlewares.push(routerMiddleware)
	// logs out redux store changes to console (dev only)
	middlewares.push(loggerMiddleware)
	return middlewares
}

export function customiseReducers(reducer: ReducersMapObject<PlatformStoreState>): ReducersMapObject<StoreState> {
	// TODO: where it's using?
	reducer.router = connectRouter(history)
	return reducer
}

/**
 * API configuration
 */

export function getDefaultApiConfiguration(): Api.Configuration {
	return new Api.Configuration({
		// these cannot be functions, this means we need to create a new Api when changing app id (new configuration)
		username: projectId,
		password: token,
	})
}

export const getApiHeaders = (): Record<string, string> => {
	const headers = {
		'Stqry-Project-Type': 'mobile_web',
	}
	if (getDemoModeState()) {
		headers['Stqry-Demo-Mode'] = 'true'
	}
	return headers
}

export const getDirectApiBaseUrl = (): string => `https://${ENV.API_HOST}`

export const getCdnApiBaseUrl = (): string => baseServer.startsWith('https://') ? baseServer : `https://${baseServer}`

export const isRTL = (languageCode?: string): boolean => {
	if (!languageCode) {
		languageCode = getCurrentLanguage()
	}

	return ['he', 'ar', 'fa'].includes(languageCode)
}

/**
 * Strings / Device language
 */

export interface Style {
	'--background-color': string
	'--background-color-hover': string
	'--background-image': string
	'--content-background-image': string
	'--extended-background-color': string
	'--body-color': string
	'--body-color-hover': string
	'--extended-body-color': string
	'--secondary-color': string
	'--placeholder-bg': string
	'--nav-bg': string
	'--tab-bg': string
	'--tab-content-color': string
	'--tab-content-color-selected': string
	'--button-bg': string
	'--button-bg-hover': string
	'--button-action-bg': string
	'--warning-color': string
	'--button-secondary-bg': string
	'--button-secondary-bg-hover': string
	'--button-secondary-border': string
	'--border-base-rbg': string
	'--border-color': string
	'--border-light': string
	'--border-overlay': string
	'--map-content-shadow': string
	'--border-shadow': string
	'--card-box-shadow-1': string
	'--card-box-shadow-2': string
	'--card-box-shadow-3': string

	'--content-margin-horizontal'?: string
	'--max-page-width'?: string
}

/**
 * Sets the language to display static strings in. Made into function as called from multiple places.
 * @param languageCode the language to display strings in
 */
export function changeLocalizationLanguage(languageCode: string) {
	// there is some issue with {languageCode}
	// use this method as callback and set local lang code
	strings.setLanguage(settings.appLanguage.currentLocale || languageCode)
}

const setUpStyles = (metaData: AppMetaData) => {
	const secondaryBgColor = colorMix(
		metaData.project_colors.background_color,
		metaData.project_colors.button_background_color,
		15,
		40,
	)

	const isBgDark = isDark(metaData.project_colors.background_color)

	let backgroundImageSrc = ''

	if (metaData.icon_media_item?.file) {
		backgroundImageSrc = generateImageUrl(metaData.icon_media_item?.file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			blur: 10,
		})
	}

	let contentBackgroundImageSrc = ''

	if (metaData.background_image_media_item) {
		contentBackgroundImageSrc = generateImageUrl(metaData.background_image_media_item?.file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			focalPoint: metaData.background_image_media_item.file?.focal_point
		})
	}

	const styles: Style = {
		'--background-color': metaData.project_colors.background_color,
		'--background-color-hover': darkenOrLighten(metaData.project_colors.background_color, 3),
		'--background-image': `url(${backgroundImageSrc})`,
		'--content-background-image': `url(${contentBackgroundImageSrc})`,
		'--extended-background-color': darkenOrLighten(metaData.project_colors.background_color, 10),
		'--body-color': metaData.project_colors.body_text_color,
		'--body-color-hover': fade(metaData.project_colors.body_text_color, 0.4),
		'--extended-body-color': darkenOrLighten(metaData.project_colors.body_text_color, 10),
		'--secondary-color': colorMix(
			metaData.project_colors.background_color,
			metaData.project_colors.button_background_color,
			15,
			40,
		),
		'--placeholder-bg': fade(metaData.project_colors.body_text_color, 0.17),
		'--nav-bg': metaData.project_colors.header_color,
		'--tab-bg': metaData.project_colors.tab_bar_color,
		'--tab-content-color': fade(metaData.project_colors.tab_bar_content_color, 0.65),
		'--tab-content-color-selected': metaData.project_colors.tab_bar_content_color,
		'--button-bg': metaData.project_colors.button_background_color,
		'--button-bg-hover': fade(metaData.project_colors.button_background_color, 0.7),
		'--button-action-bg': metaData.project_colors.action_button_background_color,
		'--warning-color': metaData.project_colors.warning_background_color,
		'--button-secondary-bg': secondaryBgColor,
		'--button-secondary-bg-hover': fade(secondaryBgColor, 0.7),
		'--button-secondary-border': colorMix(secondaryBgColor, metaData.project_colors.body_text_color, 20, 10),
		'--border-base-rbg': metaData.project_colors.border_base_color,
		'--border-color': fade(metaData.project_colors.body_text_color, isBgDark ? 0.3 : 0.15),
		'--border-light': fade(metaData.project_colors.body_text_color, isBgDark ? 0.2 : 0.1),
		'--border-overlay': fade(metaData.project_colors.body_text_color, isBgDark ? 0.32 : 0.18),
		'--map-content-shadow': 'rgba(0,0,0,0.1)',
		'--border-shadow': fade(metaData.project_colors.border_base_color, 0.18),
		'--card-box-shadow-1': fade(metaData.project_colors.body_text_color, 0.05),
		'--card-box-shadow-2': fade(metaData.project_colors.body_text_color, 0.03),
		'--card-box-shadow-3': fade(metaData.project_colors.body_text_color, 0.1),
	}

	for (const propertyName of Object.keys(styles)) {
		document.documentElement.style.setProperty(propertyName, styles[propertyName])
	}
}

export const getStyleProperty = (propertyValue: keyof Style): string =>
	document.documentElement.style.getPropertyValue(propertyValue) ||
	getComputedStyle(document.documentElement).getPropertyValue(propertyValue)

/**
 * Theme/Styles
 */

export function changeTheme(metaData: AppMetaData) {
	setUpStyles(metaData)
}

/**
 * Download file
 */

/**
 * Downloads an audio file and saves to internal storage. The result includes the path to downloaded file.
 * @param appId the app id - used in file path
 * @param collectionId the collection ite - used in file path
 * @param url the url of audio file to download
 * @returns Promise that returns the path to download file if successful
 */
export function downloadFile(_type: FileType, _fileName: string, url: string): Promise<string> {
	return new Promise((resolve, reject) => {
		try {
			resolve(url.split('?')[0])
		} catch (error) {
			reject(error)
		}
	})
}

/**
 * Calculates the download path for a file with given info
 * @param collectionId collection id
 * @param collectionItemId collection item id
 * @param fileName name of file including file suffix
 */
export function getFileDownloadPath(type: FileType, fileName: string): string {
	console.log('TODO getFileDownloadPath', type, fileName)
	return ''
}

/**
 * PreFetches an Image file and saves to disk cache as part of the Sync Content feature. The result includes whether operation was successful.
 * @param id the image id - used in url
 * @param filename the image file name - used in url
 * @param isForVideo `true` if url is for video thumbnail (url is different path)
 * @returns Promise that returns `true` if successful
 */
export function preDownloadImage(id: number, filename: string, isForVideo: boolean): Promise<boolean> {
	console.log('TODO preDownloadImage', id, filename, isForVideo)
	return Promise.resolve(true)
}

export const getPlatform = (): DevicePlatform => DEVICE_PLATFORM_KEY_MOBILE_WEB

export function getDeviceUUID(): string {
	return '1' // DeviceInfo.getUniqueID()
}

export const setDataToLocalStorage = (key: string, data: any, persist?: boolean): Promise<void> =>
	new Promise((resolve, reject) => {
		try {
			const value = JSON.stringify(data)

			if (persist) {
				localStorage.setItem(key, value)
			} else {
				sessionStorage.setItem(key, value)
			}
			resolve()
		} catch (error) {
			reject(error)
		}
	})

export const getDataFromLocalStorage = (key: string, persist?: boolean, skipVersion?: boolean): Promise<any> =>
	new Promise((resolve, reject) => {
		try {
			if (skipVersion) {
				const storageItems = persist ? { ...localStorage } : { ...sessionStorage }
				let result

				Object.keys(storageItems).forEach(itemKey => {
					if (itemKey.startsWith(key)) {
						const data = persist ? localStorage.getItem(itemKey) : sessionStorage.getItem(itemKey)
						result = JSON.parse(data)
						resolve(result)
						return
					}
				})

				if (!result) {
					resolve(undefined)
					return
				}
			} else {
				const data = persist ? localStorage.getItem(key) : sessionStorage.getItem(key)
				const result = JSON.parse(data)
				resolve(result)
			}
		} catch (error) {
			reject(error)
		}
	})

export const removeDataFromLocalStorage = (key: string | string[], persist?: boolean): Promise<void> =>
	new Promise((resolve, reject) => {
		try {
			const storageItems = persist ? { ...localStorage } : { ...sessionStorage }

			Object.keys(storageItems).forEach(itemKey => {
				if (Array.isArray(key)) {
					key.forEach(
						item =>
							itemKey.startsWith(item) &&
							(persist ? localStorage.removeItem(itemKey) : sessionStorage.removeItem(itemKey)),
					)
				} else {
					if (itemKey.startsWith(key)) {
						if (persist) {
							localStorage.removeItem(itemKey)
						} else {
							sessionStorage.removeItem(itemKey)
						}
					}
				}
			})
			resolve()
		} catch (error) {
			reject(error)
		}
	})

export const getCurrentLanguage = (): string => getCurrentLanguageCode(getStore().getState())

export const getDirMode = (): 'rtl' | 'ltr' => {
	let dirMode: 'rtl' | 'ltr'

	try {
		dirMode = isRTL() ? 'rtl' : 'ltr'
	} catch {
		dirMode = 'ltr'
	}

	return dirMode
}

export function generateMapboxStyleUrl(styleId: string): string {
	const mapboxUserId: string = import.meta.env.VITE_MAP_USER_ID
	return `mapbox://styles/${mapboxUserId}/${styleId}`
}

export const formatCurrency = (currency: string, value: number) => {
	const formatter = new Intl.NumberFormat(getCurrentLanguage(), {
		style: 'currency',
		currency,
	})

	if (!value) {
		return `${strings.free}`.toUpperCase()
	}

	return formatter.format(value / 100) // we don't need to use cents
}

export const hexToRGB = (hex: string, opacity = 1) => {
	const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

	if (match) {
		return `rgba(${parseInt(match[1], 16)},${parseInt(match[2], 16)},${parseInt(match[3], 16)},${opacity})`
	}

	return undefined
}

export const isWideImage = (file: FileRec): boolean => {
	if (file?.width > file?.height) {
		return true
	}

	return false
}

const getFormattedDuration = (data: FormattedDurationObject) => {
	// Object {data} has three properties (days, hours, minutes)
	// We are getting only two (days and hours) or (hours or minutes)
	// So, we need to get translation string, depends on properties
	const dataKeys = Object.keys(data)
	const key1 = dataKeys[0]
	const key2 = dataKeys[1]

	const singularKey1 = key1 && key1.substring(0, key1.length - 1)
	const singularKey2 = key2 && key2.substring(0, key2.length - 1)

	let keyEndpoint

	if (data[key1] > 1 && data[key2] > 1) {
		// days, hours or hours, minutes
		keyEndpoint = `${key1}_and_${key2}`
	} else if (data[key1] > 1 && data[key2] === 1) {
		// days, hour or hours, minute
		keyEndpoint = `${key1}_and_${singularKey2}`
	} else if (data[key1] === 1 && data[key2] > 1) {
		// day, hours or hour, minutes
		keyEndpoint = `${singularKey1}_and_${key2}`
	} else if (data[key1] === 1 && data[key2] === 1) {
		// day, hour or hour, minute
		keyEndpoint = `${singularKey1}_and_${singularKey2}`
	} else if (data[key2] === 1) {
		// only hour or minute
		keyEndpoint = singularKey2
	} else if (data[key2] > 1) {
		// only hours or minutes
		keyEndpoint = key2
	} else if (data[key1] === 1) {
		// only day, hour
		keyEndpoint = singularKey1
	} else if (data[key1] > 1) {
		// only days, hours
		keyEndpoint = key1
	}

	return keyEndpoint ? strings.formatString(strings[`duration_${keyEndpoint}`], { ...data }).toString() : ''
}

enum Unit {
	Imperial = 'imperial',
	Metric = 'metric',
}
export type UnitType = Unit.Imperial | Unit.Metric

/**
 * @deprecated use distance unit from app settings
 */
export const getDistanceUnit = (): UnitType => {
	const language = getCurrentLanguage()
	const country = language && typeof language === 'string' ? language.split('-')[1] : undefined

	if (country !== undefined && ['uk', 'gb', 'us', 'mm', 'lr'].includes(country.toLowerCase())) {
		return Unit.Imperial
	}

	return Unit.Metric
}

export const format = {
	duration: (duration: number): string => {
		if (!duration || isNaN(duration)) {
			return ''
		}

		// 1440 - minutes in day (60 * 24)
		const durationDays = Math.floor(duration / 1440) || null
		const durationHours = Math.floor((duration - durationDays * 1440) / 60) || null
		const durationMinutes = Math.round(duration % 60) || null

		if (durationDays) {
			return getFormattedDuration({
				days: durationDays,
				hours: durationHours,
			})
		} else if (durationHours) {
			return getFormattedDuration({
				hours: durationHours,
				minutes: durationMinutes,
			})
		} else if (durationMinutes) {
			return getFormattedDuration({
				minutes: durationMinutes,
			})
		} else {
			return ''
		}
	},
	distance: (meters: number, distanceUnit: GetAppShowResponse.DistanceUnitEnum): string => {
		switch (distanceUnit) {
			case GetAppShowResponse.DistanceUnitEnum.Mi: {
				const mi = meters / 1609.344
				if (mi >= 0.1) {
					return `${parseFloat(mi.toFixed(mi >= 100 ? 0 : 1))} ${strings.distance_miles}`
				}
				return `${(mi * 5280).toFixed(0)} ${strings.distance_feet}`
			}
			case GetAppShowResponse.DistanceUnitEnum.Km: {
				if (meters >= 500) {
					return `${parseFloat((meters / 1000).toFixed(meters >= 100000 ? 0 : 1))} ${strings.distance_kilometers}`
				}
				return `${meters.toFixed(0)} ${strings.distance_meters}`
			}
		}
	},
}

export const getErrorMessage = (error: Error): string | undefined => {
	if (error.message === 'Network request failed') {
		return strings.network_error
	} else {
		return strings.error_occurred
	}
}

export const formatNumberStyle = (property: number | string) =>
	typeof property === 'string' || (!isNaN(property) && property !== null) ? property : undefined

export const convertCamelCaseKeysToSnakeCase = (obj: any): any => {
	const snakeCaseParams = {}
	Object.keys(obj).forEach(key => {
		const snakeCaseKey = key
			.replace(/([A-Z])/g, ' $1')
			.split(' ')
			.join('_')
			.toLowerCase()
		snakeCaseParams[snakeCaseKey] = obj[key]
	})

	return snakeCaseParams
}

export const validateUrl = (link: string): URL => {
	let url: URL
	try {
		url = new URL(link)
	} catch (error) {
		console.warn('url not valid')
	}

	return url
}

export const waitForSomething = (callback: () => unknown, timeout = 500, maxAttempts = 10): Promise<unknown> =>
	new Promise((resolve, reject) => {
		let attempts = 0
		const waitingInterval = setInterval(() => {
			if (attempts === maxAttempts) {
				clearInterval(waitingInterval)
				reject()
			}

			if (callback()) {
				clearInterval(waitingInterval)
				resolve(callback())
			} else {
				attempts++
			}
		}, timeout)
	})

export const isPageInsideIFrame = (): boolean => window.location !== window.parent.location

export const isSafariBrowser = (): boolean => {
	const browser = getUAParser().getBrowser()
	if (browser.name === 'Safari' || browser.name === 'Mobile Safari') {
		return true
	}
	return
}

export const isIOSSafariBrowser = (): boolean => {
	const browser = getUAParser().getBrowser()
	if (browser.name === 'Mobile Safari') {
		return true
	}
	return false
}

export const isMobileOrTabletDevice = (): boolean => {
	const device = getUAParser().getDevice()
	return device.type === 'mobile' || device.type === 'tablet'
}

export const containsEmbeddedURL = (url: string): boolean => {
	const res = url.match(/=(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:\/?#[\]@!\$&'\(\)\*\+,;=.%]+/g)
	return res !== null
}
